import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import api, { API_BASE, getAccessToken, getSessionId } from "../lib/api";
import { useToasts } from "./Toasts";
import {
  HIGHLIGHT_COLORS,
  MESH_SAMPLE_NAME,
  clampBrushScale,
  classifyMeshName,
  dataUrlToBlob,
  formatMillimetres,
  listMeshesFromRecord,
  reprocessStatusLabel,
  validateReprocessNote,
} from "../lib/meshInspect";

const SAMPLE_MESH = {
  id: "sample",
  filename: MESH_SAMPLE_NAME,
  format: "stl",
  origin: "sample",
  downloadPath: "/samples/forgelinc-wedge.stl",
};

function asArrayBuffer(data) {
  if (data instanceof ArrayBuffer) return data;
  if (ArrayBuffer.isView(data)) return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
  throw new Error("Mesh bytes were not readable.");
}

function parseGltf(buffer) {
  const loader = new GLTFLoader();
  return new Promise((resolve, reject) => {
    loader.parse(buffer, "", resolve, reject);
  });
}

async function postForm(path, body) {
  const headers = {};
  const token = getAccessToken();
  const sessionId = getSessionId();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (sessionId) headers["X-Session-Id"] = sessionId;
  const response = await fetch(`${API_BASE}${path}`, { method: "POST", headers, body });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = payload?.detail;
    throw new Error(typeof detail === "string" && detail.trim() ? detail : "Could not save the mesh package.");
  }
  return payload;
}

function createMeshEngine(mount) {
  const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true, alpha: false });
  renderer.localClippingEnabled = true;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setClearColor(0x050814, 1);
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x050814);
  const camera = new THREE.PerspectiveCamera(40, 1, 0.01, 100);
  camera.position.set(2.2, 1.4, 2.6);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.screenSpacePanning = true;
  controls.target.set(0, 0, 0);
  const hemi = new THREE.HemisphereLight(0xf4f7fb, 0x1b2433, 1.15);
  scene.add(hemi);
  const key = new THREE.DirectionalLight(0xffffff, 1.35);
  key.position.set(3, 5, 4);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0x9ec5ff, 0.45);
  fill.position.set(-4, 1.5, -2);
  scene.add(fill);
  const wrapper = new THREE.Group();
  scene.add(wrapper);
  const helpers = new THREE.Group();
  scene.add(helpers);
  const grid = new THREE.GridHelper(256, 16, 0x3a4a66, 0x243044);
  const plate = new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-128, 0, -128),
      new THREE.Vector3(128, 0, -128),
      new THREE.Vector3(128, 0, 128),
      new THREE.Vector3(-128, 0, 128),
    ]),
    new THREE.LineBasicMaterial({ color: 0x6ea8ff }),
  );
  helpers.add(grid);
  helpers.add(plate);
  const measureGeo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
  const measureLine = new THREE.Line(measureGeo, new THREE.LineBasicMaterial({ color: 0x3ee0ff }));
  measureLine.visible = false;
  helpers.add(measureLine);
  const clipPlane = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);
  mount.appendChild(renderer.domElement);

  const sphereGeo = new THREE.SphereGeometry(1, 10, 8);
  const paintMaterials = new Map();
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  let modelRoot = null;
  let strokes = [];
  let ops = [];
  let redoOps = [];
  let stroke = null;
  let tool = "orbit";
  let pointing = false;
  let baseRadius = 0.04;
  let brushScale = 1;
  let brushRadius = 0.04;
  let color = HIGHLIGHT_COLORS[0].value;
  let lastLocal = null;
  let running = true;
  let viewName = "iso";
  let shade = "solid";
  let showGrid = true;
  let clipOn = false;
  let clipT = 0.55;
  let measurePoints = [];
  let measureDistance = null;
  let onStrokes = () => {};
  let onHistory = () => {};
  let onReadout = () => {};
  const deskBackground = scene.background;

  function materialFor(hex) {
    const keyName = String(hex || color).toLowerCase();
    if (!paintMaterials.has(keyName)) {
      paintMaterials.set(keyName, new THREE.MeshBasicMaterial({ color: keyName }));
    }
    return paintMaterials.get(keyName);
  }

  function resize() {
    const width = mount.clientWidth;
    const height = mount.clientHeight;
    if (!width || !height) return;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
  }

  function notify() {
    onStrokes(ops.length);
    onHistory({ undo: ops.length, redo: redoOps.length });
  }

  function syncPlate() {
    const box = modelRoot ? new THREE.Box3().setFromObject(modelRoot) : null;
    const y = box ? box.min.y : 0;
    plate.position.y = y;
    grid.position.y = y;
    const size = box ? box.getSize(new THREE.Vector3()) : null;
    onReadout({
      size: size ? { x: size.x, y: size.y, z: size.z } : null,
      measure: measureDistance,
    });
  }

  function placeCamera(view) {
    viewName = view || viewName || "iso";
    const box = new THREE.Box3().setFromObject(wrapper);
    if (box.isEmpty()) return;
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const radius = Math.max(sphere.radius, 0.001);
    const distance = radius / Math.sin((camera.fov * Math.PI) / 360);
    const target = sphere.center.clone();
    const dir = {
      iso: new THREE.Vector3(0.85, 0.55, 1.15),
      top: new THREE.Vector3(0.001, 1, 0.001),
      front: new THREE.Vector3(0, 0.12, 1),
      side: new THREE.Vector3(1, 0.12, 0),
    }[viewName] || new THREE.Vector3(0.85, 0.55, 1.15);
    dir.normalize();
    camera.near = Math.max(distance / 200, 0.001);
    camera.far = Math.max(distance * 40, 2500);
    camera.position.copy(target).addScaledVector(dir, distance * 1.2);
    controls.target.copy(target);
    controls.minDistance = Math.max(distance * 0.08, 0.001);
    controls.maxDistance = Math.max(distance * 12, 480);
    camera.updateProjectionMatrix();
    camera.lookAt(controls.target);
    controls.update();
  }

  function applyClip() {
    let constant = 0;
    if (modelRoot) {
      const box = new THREE.Box3().setFromObject(modelRoot);
      constant = box.min.y + (box.max.y - box.min.y) * clipT;
    }
    clipPlane.constant = constant;
    const planes = clipOn ? [clipPlane] : [];
    modelRoot?.traverse((node) => {
      if (!node.isMesh || !node.material) return;
      const list = Array.isArray(node.material) ? node.material : [node.material];
      list.forEach((material) => {
        material.clippingPlanes = planes;
        material.needsUpdate = true;
      });
    });
  }

  function setShade(mode) {
    shade = mode === "wire" || mode === "xray" ? mode : "solid";
    if (!modelRoot) return;
    modelRoot.traverse((node) => {
      if (!node.isMesh) return;
      if (!node.userData.baseMaterial) node.userData.baseMaterial = node.material;
      if (node.userData.shadeMaterial && node.userData.shadeMaterial !== node.userData.baseMaterial) {
        node.userData.shadeMaterial.dispose?.();
        node.userData.shadeMaterial = null;
      }
      if (shade === "solid") {
        node.material = node.userData.baseMaterial;
      } else if (shade === "wire") {
        node.userData.shadeMaterial = new THREE.MeshBasicMaterial({ color: 0xd5e4ff, wireframe: true });
        node.material = node.userData.shadeMaterial;
      } else {
        node.userData.shadeMaterial = new THREE.MeshStandardMaterial({
          color: 0x9eb6d2,
          transparent: true,
          opacity: 0.32,
          depthWrite: false,
          roughness: 0.35,
          metalness: 0.02,
          side: THREE.DoubleSide,
        });
        node.material = node.userData.shadeMaterial;
      }
    });
    applyClip();
  }

  function disposeNode(root) {
    root.traverse((node) => {
      if (node.geometry && node.geometry !== sphereGeo) node.geometry.dispose();
      if (!node.material || paintMaterialsHas(node.material)) return;
      const list = Array.isArray(node.material) ? node.material : [node.material];
      list.forEach((item) => item.dispose?.());
    });
  }

  function paintMaterialsHas(material) {
    if (Array.isArray(material)) return material.some((item) => [...paintMaterials.values()].includes(item));
    return [...paintMaterials.values()].includes(material);
  }

  function clearStrokes() {
    strokes.forEach((group) => wrapper.remove(group));
    strokes = [];
    ops = [];
    redoOps = [];
    stroke = null;
    lastLocal = null;
    notify();
  }

  function frameObject(object) {
    const box = new THREE.Box3().setFromObject(object);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    if (!Number.isFinite(center.x) || size.length() === 0) {
      throw new Error("This file has no triangles to inspect.");
    }
    object.position.sub(center);
    const radius = Math.max(size.length() * 0.5, 0.001);
    baseRadius = Math.max(radius * 0.02, 0.001);
    brushRadius = baseRadius * brushScale;
    placeCamera(viewName || "iso");
    syncPlate();
  }

  async function load(buffer, format) {
    clearStrokes();
    if (modelRoot) {
      wrapper.remove(modelRoot);
      disposeNode(modelRoot);
      modelRoot = null;
    }
    const bytes = asArrayBuffer(buffer);
    let object;
    if (format === "glb" || format === "gltf") {
      const gltf = await parseGltf(bytes);
      object = gltf.scene || gltf.scenes?.[0];
      if (!object) throw new Error("This glTF has no scene.");
    } else {
      const geometry = new STLLoader().parse(bytes);
      const positions = geometry.getAttribute("position");
      if (!positions || positions.count < 3) throw new Error("This STL has no triangles.");
      geometry.computeVertexNormals();
      object = new THREE.Mesh(
        geometry,
        new THREE.MeshStandardMaterial({ color: 0xb7c3d4, roughness: 0.62, metalness: 0.08, side: THREE.DoubleSide }),
      );
    }
    modelRoot = object;
    wrapper.position.set(0, 0, 0);
    wrapper.rotation.set(0, 0, 0);
    wrapper.scale.set(1, 1, 1);
    wrapper.add(modelRoot);
    frameObject(modelRoot);
    setShade(shade);
    applyClip();
    resize();
  }

  function paintable() {
    const meshes = [];
    modelRoot?.traverse((node) => {
      if (node.isMesh) meshes.push(node);
    });
    return meshes;
  }

  function addMark(event) {
    if (!modelRoot || !stroke) return;
    if (stroke.children.length >= 500) return;
    const rect = renderer.domElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointer, camera);
    const hits = raycaster.intersectObjects(paintable(), false);
    if (!hits.length) return;
    const hit = hits[0];
    const normal = hit.face?.normal?.clone()?.transformDirection(hit.object.matrixWorld)?.normalize?.();
    const point = hit.point.clone();
    if (normal) point.addScaledVector(normal, brushRadius * 0.35);
    const local = wrapper.worldToLocal(point);
    if (lastLocal && lastLocal.distanceTo(local) < brushRadius * 0.65) return;
    lastLocal = local.clone();
    const mark = new THREE.Mesh(sphereGeo, materialFor(color));
    mark.scale.setScalar(brushRadius);
    mark.position.copy(local);
    mark.userData.paint = true;
    stroke.add(mark);
  }

  function pointerNdc(event) {
    const rect = renderer.domElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointer, camera);
    return true;
  }

  function paintMarks() {
    const marks = [];
    strokes.forEach((group) => {
      group.traverse((node) => {
        if (node.isMesh && node.userData.paint) marks.push(node);
      });
    });
    return marks;
  }

  function addMeasurePoint(event) {
    if (!pointerNdc(event)) return;
    const hits = raycaster.intersectObjects(paintable(), false);
    let point = hits[0]?.point?.clone();
    if (!point) {
      point = new THREE.Vector3();
      const bed = new THREE.Plane(new THREE.Vector3(0, 1, 0), -plate.position.y);
      if (!raycaster.ray.intersectPlane(bed, point)) return;
    }
    if (measurePoints.length >= 2) measurePoints = [];
    measurePoints.push(point);
    if (measurePoints.length === 2) {
      measureDistance = measurePoints[0].distanceTo(measurePoints[1]);
      measureGeo.setFromPoints(measurePoints);
      measureLine.visible = true;
    } else {
      measureDistance = null;
      measureLine.visible = false;
    }
    syncPlate();
  }

  function eraseAt(event) {
    if (!pointerNdc(event)) return;
    const hits = raycaster.intersectObjects(paintMarks(), false);
    if (!stroke || stroke.type !== "erase") stroke = { type: "erase", items: [] };
    hits.forEach((hit) => {
      const mesh = hit.object;
      const parent = mesh.parent;
      if (!parent) return;
      parent.remove(mesh);
      stroke.items.push({ parent, mesh });
    });
  }

  function onPointerDown(event) {
    if (event.button !== 0) return;
    if (tool === "measure") {
      event.preventDefault();
      addMeasurePoint(event);
      return;
    }
    if (tool !== "paint" && tool !== "erase") return;
    event.preventDefault();
    event.stopPropagation();
    pointing = true;
    lastLocal = null;
    redoOps = [];
    if (tool === "erase") {
      eraseAt(event);
    } else {
      stroke = new THREE.Group();
      stroke.userData.paint = true;
      wrapper.add(stroke);
      strokes = strokes.concat(stroke);
      addMark(event);
    }
    renderer.domElement.setPointerCapture?.(event.pointerId);
    notify();
  }

  function onPointerMove(event) {
    if (!pointing) return;
    if (tool !== "paint" && tool !== "erase") return;
    event.preventDefault();
    if (tool === "erase") eraseAt(event);
    else addMark(event);
  }

  function onPointerUp(event) {
    if (!pointing && stroke?.type !== "erase") return;
    pointing = false;
    if (stroke?.type === "erase") {
      if (stroke.items.length) ops.push(stroke);
      stroke = null;
      notify();
    } else if (stroke) {
      if (stroke.children.length === 0) {
        wrapper.remove(stroke);
        strokes = strokes.filter((group) => group !== stroke);
      } else {
        ops.push({ type: "paint", group: stroke });
      }
      stroke = null;
      lastLocal = null;
      notify();
    }
    if (event?.pointerId != null) renderer.domElement.releasePointerCapture?.(event.pointerId);
  }

  function onWheel(event) {
    event.preventDefault();
  }

  renderer.domElement.addEventListener("pointerdown", onPointerDown);
  renderer.domElement.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerup", onPointerUp);
  renderer.domElement.addEventListener("wheel", onWheel, { passive: false });

  const resizeObserver = new ResizeObserver(() => resize());
  resizeObserver.observe(mount);
  resize();
  let raf = 0;
  const frame = () => {
    raf = requestAnimationFrame(frame);
    if (!running) return;
    controls.update();
    renderer.render(scene, camera);
  };
  frame();

  function renderStill() {
    controls.update();
    renderer.render(scene, camera);
    return renderer.domElement.toDataURL("image/png");
  }

  function capture() {
    if (!modelRoot) throw new Error("Load a mesh before sending.");
    const savedPosition = camera.position.clone();
    const savedTarget = controls.target.clone();
    const wasDamping = controls.enableDamping;
    const helpersVisible = helpers.visible;
    controls.enableDamping = false;
    helpers.visible = false;
    const current = renderStill();
    const offset = camera.position.clone().sub(controls.target);
    offset.applyAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI * 0.65);
    camera.position.copy(controls.target).add(offset);
    camera.lookAt(controls.target);
    const orbit = renderStill();
    camera.position.copy(savedPosition);
    controls.target.copy(savedTarget);
    camera.lookAt(controls.target);
    controls.update();
    const hidden = modelRoot.visible;
    modelRoot.visible = false;
    scene.background = new THREE.Color(0x000000);
    const mask = renderStill();
    modelRoot.visible = hidden;
    scene.background = deskBackground;
    helpers.visible = helpersVisible;
    controls.enableDamping = wasDamping;
    controls.update();
    renderer.render(scene, camera);
    return { current, orbit, mask };
  }

  function applyTool(next) {
    tool = next || "orbit";
    const grabbing = tool === "paint" || tool === "erase" || tool === "measure";
    controls.enabled = !grabbing;
    renderer.domElement.classList.toggle("is-painting", tool === "paint" || tool === "erase");
    renderer.domElement.classList.toggle("is-measuring", tool === "measure");
    if (!grabbing) onPointerUp();
  }

  return {
    resize,
    load,
    capture,
    hasModel: () => Boolean(modelRoot),
    setRunning(next) {
      running = Boolean(next);
      if (running) {
        resize();
        requestAnimationFrame(resize);
      }
    },
    setTool(next) {
      applyTool(next);
    },
    setPainting(next) {
      applyTool(next ? "paint" : "orbit");
    },
    setColor(next) {
      color = next || HIGHLIGHT_COLORS[0].value;
    },
    setBrushScale(next) {
      brushScale = clampBrushScale(next);
      brushRadius = baseRadius * brushScale;
    },
    setLight(next) {
      if (next === "soft") {
        hemi.intensity = 1.45;
        key.intensity = 0.5;
        fill.intensity = 0.4;
      } else if (next === "contrast") {
        hemi.intensity = 0.28;
        key.intensity = 2.2;
        fill.intensity = 0.12;
      } else {
        hemi.intensity = 1.15;
        key.intensity = 1.35;
        fill.intensity = 0.45;
      }
    },
    setGrid(next) {
      showGrid = Boolean(next);
      grid.visible = showGrid;
      plate.visible = showGrid;
    },
    setClip(on, t) {
      clipOn = Boolean(on);
      if (Number.isFinite(Number(t))) clipT = Math.min(1, Math.max(0, Number(t)));
      applyClip();
    },
    setOnStrokes(fn) {
      onStrokes = typeof fn === "function" ? fn : () => {};
    },
    setOnHistory(fn) {
      onHistory = typeof fn === "function" ? fn : () => {};
    },
    setOnReadout(fn) {
      onReadout = typeof fn === "function" ? fn : () => {};
      syncPlate();
    },
    fit(view) {
      placeCamera(view || viewName || "iso");
      syncPlate();
    },
    rotate90() {
      wrapper.rotateY(Math.PI / 2);
      wrapper.updateMatrixWorld(true);
      placeCamera(viewName);
      syncPlate();
    },
    setScalePercent(percent) {
      const next = Math.min(400, Math.max(10, Number(percent) || 100)) / 100;
      wrapper.scale.setScalar(next);
      wrapper.updateMatrixWorld(true);
      placeCamera(viewName);
      syncPlate();
    },
    dropToBed() {
      wrapper.position.y = 0;
      wrapper.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(modelRoot || wrapper);
      wrapper.position.y -= box.min.y;
      wrapper.updateMatrixWorld(true);
      syncPlate();
    },
    layFlat() {
      const candidates = [
        [0, 0, 0],
        [Math.PI / 2, 0, 0],
        [-Math.PI / 2, 0, 0],
        [0, 0, Math.PI / 2],
        [0, 0, -Math.PI / 2],
        [Math.PI, 0, 0],
      ];
      let best = candidates[0];
      let bestHeight = Infinity;
      candidates.forEach((euler) => {
        wrapper.rotation.set(euler[0], euler[1], euler[2]);
        wrapper.updateMatrixWorld(true);
        const size = new THREE.Box3().setFromObject(modelRoot || wrapper).getSize(new THREE.Vector3());
        if (size.y < bestHeight) {
          bestHeight = size.y;
          best = euler;
        }
      });
      wrapper.rotation.set(best[0], best[1], best[2]);
      wrapper.updateMatrixWorld(true);
      this.dropToBed();
      placeCamera(viewName);
    },
    setShade,
    screenshot() {
      return renderStill();
    },
    undo() {
      const op = ops.pop();
      if (!op) return;
      if (op.type === "erase") op.items.forEach(({ parent, mesh }) => parent.add(mesh));
      else if (op.group) {
        wrapper.remove(op.group);
        strokes = strokes.filter((group) => group !== op.group);
      }
      redoOps.push(op);
      stroke = null;
      notify();
    },
    redo() {
      const op = redoOps.pop();
      if (!op) return;
      if (op.type === "erase") op.items.forEach(({ parent, mesh }) => parent.remove(mesh));
      else if (op.group) {
        wrapper.add(op.group);
        strokes = strokes.concat(op.group);
      }
      ops.push(op);
      notify();
    },
    clear() {
      clearStrokes();
    },
    dispose() {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      renderer.domElement.removeEventListener("wheel", onWheel);
      controls.dispose();
      clearStrokes();
      if (modelRoot) disposeNode(modelRoot);
      sphereGeo.dispose();
      grid.geometry?.dispose();
      plate.geometry?.dispose();
      measureGeo.dispose();
      paintMaterials.forEach((material) => material.dispose());
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}

export default function MeshInspectViewport({ record, active = true, onRecord, preferMeshId = "" }) {
  const toast = useToasts((s) => s.push);
  const mountRef = useRef(null);
  const engineRef = useRef(null);
  const [selectedId, setSelectedId] = useState("");
  const [tool, setTool] = useState("orbit");
  const [color, setColor] = useState(HIGHLIGHT_COLORS[0].value);
  const [shade, setShade] = useState("solid");
  const [light, setLight] = useState("studio");
  const [showGrid, setShowGrid] = useState(true);
  const [clipOn, setClipOn] = useState(false);
  const [clipT, setClipT] = useState(0.55);
  const [brushScale, setBrushScale] = useState(1);
  const [scalePct, setScalePct] = useState(100);
  const [sizeLabel, setSizeLabel] = useState("");
  const [measureLabel, setMeasureLabel] = useState("");
  const [redoCount, setRedoCount] = useState(0);
  const [note, setNote] = useState("");
  const [strokeCount, setStrokeCount] = useState(0);
  const [modelReady, setModelReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pkg, setPkg] = useState(null);
  const [handoff, setHandoff] = useState(null);
  const appliedPrefer = useRef("");
  const meshes = useMemo(() => listMeshesFromRecord(record), [record]);
  const selectedMesh = selectedId === "sample" ? SAMPLE_MESH : meshes.find((row) => row.id === selectedId) || null;

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;
    let engine;
    try {
      engine = createMeshEngine(mount);
    } catch (err) {
      setLoadError(err?.message || "This browser could not start the 3D viewer.");
      return undefined;
    }
    engine.setOnStrokes(setStrokeCount);
    engine.setOnHistory((history) => {
      setStrokeCount(history.undo || 0);
      setRedoCount(history.redo || 0);
    });
    engine.setOnReadout((readout) => {
      setSizeLabel(formatMillimetres(readout?.size));
      setMeasureLabel(Number.isFinite(readout?.measure) ? `${readout.measure.toFixed(1)} mm` : "");
    });
    engineRef.current = engine;
    return () => {
      engine.dispose();
      engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    engineRef.current?.setRunning(active);
    if (active) engineRef.current?.resize();
  }, [active]);

  useEffect(() => {
    engineRef.current?.setTool(active ? tool : "orbit");
  }, [tool, active]);

  useEffect(() => {
    engineRef.current?.setBrushScale(brushScale);
  }, [brushScale]);

  useEffect(() => {
    engineRef.current?.setLight(light);
  }, [light]);

  useEffect(() => {
    engineRef.current?.setGrid(showGrid);
  }, [showGrid]);

  useEffect(() => {
    engineRef.current?.setShade(shade);
  }, [shade]);

  useEffect(() => {
    engineRef.current?.setClip(clipOn, clipT);
  }, [clipOn, clipT]);

  useEffect(() => {
    engineRef.current?.setColor(color);
  }, [color]);

  useEffect(() => {
    if (!preferMeshId || appliedPrefer.current === preferMeshId) return;
    if (!meshes.some((row) => row.id === preferMeshId)) return;
    appliedPrefer.current = preferMeshId;
    setSelectedId(preferMeshId);
  }, [preferMeshId, meshes]);

  useEffect(() => {
    if (!active || selectedId) return;
    if (meshes[0]) setSelectedId(meshes[0].id);
  }, [active, meshes, selectedId]);

  useEffect(() => {
    if (!selectedId || selectedId === "sample") return;
    if (!meshes.some((row) => row.id === selectedId)) setSelectedId(meshes[0]?.id || "");
  }, [meshes, selectedId]);

  useEffect(() => {
    const existing = record?.metadata?.print_loop?.mesh_reprocess;
    if (existing && typeof existing === "object") setPkg(existing);
    const recipe = record?.metadata?.print_loop?.orca?.purge_recipe;
    if (recipe && typeof recipe === "object") setHandoff(recipe);
  }, [record]);

  useEffect(() => {
    if (tool === "orbit") return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setTool("orbit");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tool]);

  const selectedMeshRef = useRef(null);
  selectedMeshRef.current = selectedMesh;
  const meshKey = selectedMesh ? `${selectedMesh.id}|${selectedMesh.format}|${selectedMesh.downloadPath}` : "";
  useEffect(() => {
    const mesh = selectedMeshRef.current;
    if (!meshKey || !record?.id || !mesh) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoadError("");
      setModelReady(false);
      try {
        let buffer;
        if (mesh.id === "sample") {
          const response = await fetch(mesh.downloadPath);
          if (!response.ok) {
            const { data } = await api.get("/businesses/linkforge/sample-mesh", { responseType: "arraybuffer" });
            buffer = data;
          } else {
            buffer = await response.arrayBuffer();
          }
        } else {
          const { data } = await api.get(mesh.downloadPath, { responseType: "arraybuffer" });
          buffer = data;
        }
        if (cancelled) return;
        const format = mesh.format === "3mf" ? "stl" : mesh.format;
        await engineRef.current?.load(buffer, format);
        if (!cancelled) setModelReady(true);
      } catch (err) {
        if (!cancelled) {
          setModelReady(false);
          setLoadError(err?.message || "Could not load this mesh.");
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [meshKey, record?.id]);

  const onFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !record?.id || busy) return;
    const info = classifyMeshName(file.name);
    if (!info.format) {
      setLoadError(info.reason === "sliced"
        ? "Sliced .gcode.3mf files are toolpaths, not a viewable mesh."
        : "Use an STL, GLB, glTF, or 3MF mesh.");
      return;
    }
    setBusy(true);
    setLoadError("");
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("notes", file.name);
      const payload = await postForm(`/businesses/linkforge/records/${record.id}/meshes/upload`, body);
      onRecord?.(payload.record);
      if (payload.mesh?.id) setSelectedId(payload.mesh.id);
      toast("Mesh saved on this product.", "ok");
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const problem = validateReprocessNote(note);
    if (problem) {
      setLoadError(problem);
      return;
    }
    if (!engineRef.current?.hasModel()) {
      setLoadError("Load a mesh before sending.");
      return;
    }
    setBusy(true);
    setLoadError("");
    try {
      const shots = engineRef.current.capture();
      const body = new FormData();
      body.append("note", note.trim());
      body.append("highlight_color", color);
      body.append("source_artifact_id", selectedMesh?.id && selectedMesh.id !== "sample" ? selectedMesh.id : "");
      body.append("source_filename", selectedMesh?.filename || MESH_SAMPLE_NAME);
      body.append("source_format", selectedMesh?.format || "stl");
      body.append("source_origin", selectedMesh?.origin || "sample");
      body.append("angle_current", dataUrlToBlob(shots.current), "angle-current.png");
      body.append("angle_orbit", dataUrlToBlob(shots.orbit), "angle-orbit.png");
      body.append("mask", dataUrlToBlob(shots.mask), "paint-mask.png");
      const payload = await postForm(`/businesses/linkforge/records/${record.id}/mesh-reprocess`, body);
      onRecord?.(payload.record);
      setPkg(payload.package);
      setTool("orbit");
      toast("Reprocess package stored. Ready to send.", "ok");
    } catch (err) {
      setLoadError(err.message || "Could not store the reprocess package.");
    } finally {
      setBusy(false);
    }
  };

  const sendToOrca = async () => {
    if (!record?.id || busy) return;
    if (!selectedMesh || selectedMesh.id === "sample") {
      setLoadError("Load a stored STL, GLB, or 3MF before sending to Orca.");
      return;
    }
    setBusy(true);
    setLoadError("");
    try {
      const body = new FormData();
      body.append("artifact_id", selectedMesh.id);
      const payload = await postForm(`/businesses/linkforge/records/${record.id}/print-loop/orca/from-mesh`, body);
      onRecord?.(payload.record);
      const recipe = payload.purge_recipe || payload.package?.purge_recipe || null;
      setHandoff(recipe);
      const warning = recipe?.warnings?.[0];
      toast(warning || recipe?.note || "Orca handoff saved.", warning ? "err" : "ok");
    } catch (err) {
      setLoadError(err.message || "Could not send this mesh to Orca.");
    } finally {
      setBusy(false);
    }
  };

  const downloadSlot = async (slot) => {
    const file = pkg?.files?.[slot];
    if (!file?.download_path) return;
    const { data } = await api.get(file.download_path, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.filename || `${slot}.png`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const status = pkg ? `${reprocessStatusLabel(pkg)}. ${pkg.destination_note || "ForgeLinc did not call Meshy."}` : "";

  return (
    <div className={`fld-mesh-host${active ? "" : " is-hidden"}`} data-testid="forgelink-mesh-stage" data-edit-name="Design Desk 3D viewer" hidden={!active}>
      <div ref={mountRef} className={`fld-mesh-view${tool === "paint" || tool === "erase" ? " is-painting" : ""}`} />
      <div className="fld-mesh-top">
        <select
          value={selectedId}
          onChange={(event) => setSelectedId(event.target.value)}
          aria-label="Mesh to inspect"
          data-testid="forgelink-mesh-select"
        >
          <option value="">Choose a mesh</option>
          <option value="sample">Sample wedge</option>
          {meshes.map((row) => (
            <option value={row.id} key={row.id}>{row.filename}</option>
          ))}
        </select>
        <button type="button" className="btn small" onClick={() => setSelectedId("sample")} data-testid="forgelink-mesh-sample">
          Open sample
        </button>
        <label className="fld-mesh-file">
          Load mesh
          <input type="file" accept=".stl,.glb,.gltf,.3mf,model/stl,model/gltf-binary,model/gltf+json,model/3mf" onChange={onFile} disabled={busy} data-testid="forgelink-mesh-file" />
        </label>
        {sizeLabel ? <span className="fld-mesh-size" data-testid="forgelink-mesh-size">{sizeLabel}</span> : null}
        {measureLabel ? <span className="fld-mesh-size" data-testid="forgelink-mesh-measure-readout">{measureLabel}</span> : null}
      </div>
      <div className="fld-mesh-tools" data-testid="forgelink-mesh-tools">
        <button type="button" className="btn small" disabled={!modelReady} onClick={() => engineRef.current?.fit("iso")} data-testid="forgelink-mesh-fit">Fit</button>
        {["top", "front", "side", "iso"].map((view) => (
          <button type="button" className="btn small" key={view} disabled={!modelReady} onClick={() => engineRef.current?.fit(view)} data-testid={`forgelink-mesh-view-${view}`}>
            {view}
          </button>
        ))}
        <button type="button" className="btn small" disabled={!modelReady} onClick={() => setShade((value) => (value === "solid" ? "wire" : value === "wire" ? "xray" : "solid"))} data-testid="forgelink-mesh-shade">
          {shade === "solid" ? "Solid" : shade === "wire" ? "Wireframe" : "X-ray"}
        </button>
        <button type="button" className="btn small" disabled={!modelReady} onClick={() => setLight((value) => (value === "studio" ? "soft" : value === "soft" ? "contrast" : "studio"))} data-testid="forgelink-mesh-light">
          {light === "studio" ? "Studio" : light === "soft" ? "Soft" : "Contrast"}
        </button>
        <button type="button" className={`btn small${showGrid ? " primary" : ""}`} aria-pressed={showGrid} onClick={() => setShowGrid((value) => !value)} data-testid="forgelink-mesh-grid">Plate</button>
        <button type="button" className={`btn small${tool === "measure" ? " primary" : ""}`} aria-pressed={tool === "measure"} onClick={() => setTool((value) => (value === "measure" ? "orbit" : "measure"))} data-testid="forgelink-mesh-measure">Measure</button>
        <button type="button" className={`btn small${clipOn ? " primary" : ""}`} aria-pressed={clipOn} onClick={() => setClipOn((value) => !value)} data-testid="forgelink-mesh-clip">Section</button>
        {clipOn ? <input type="range" min="0.05" max="0.95" step="0.01" value={clipT} aria-label="Section height" onChange={(event) => setClipT(Number(event.target.value))} data-testid="forgelink-mesh-clip-range" /> : null}
        <button type="button" className="btn small" disabled={!modelReady} title="View only. Does not change the file sent to Orca." onClick={() => engineRef.current?.rotate90()} data-testid="forgelink-mesh-rotate">Rotate 90°</button>
        <label className="fld-mesh-scale" title="View only. Does not change the file sent to Orca.">
          Scale
          <input type="range" min="10" max="200" value={scalePct} onChange={(event) => { const next = Number(event.target.value); setScalePct(next); engineRef.current?.setScalePercent(next); }} data-testid="forgelink-mesh-scale" />
        </label>
        <button type="button" className="btn small" disabled={!modelReady} title="View only. Does not change the file sent to Orca." onClick={() => engineRef.current?.layFlat()} data-testid="forgelink-mesh-flat">Lay flat</button>
        <button type="button" className="btn small" disabled={!modelReady} title="View only. Does not change the file sent to Orca." onClick={() => engineRef.current?.dropToBed()} data-testid="forgelink-mesh-drop">Drop to bed</button>
        <button type="button" className="btn small" disabled={!modelReady} onClick={() => {
          const url = engineRef.current?.screenshot();
          if (!url) return;
          const link = document.createElement("a");
          link.href = url;
          link.download = "forgelinc-view.png";
          link.click();
        }} data-testid="forgelink-mesh-shot">Screenshot</button>
      </div>
      <div className="fld-mesh-hud">
        <button
          type="button"
          className={`btn small${tool === "paint" ? " primary" : ""}`}
          aria-pressed={tool === "paint"}
          onClick={() => setTool((value) => (value === "paint" ? "orbit" : "paint"))}
          data-testid="forgelink-mesh-paint"
        >
          {tool === "paint" ? "Stop paint" : "Paint"}
        </button>
        <button
          type="button"
          className={`btn small${tool === "erase" ? " primary" : ""}`}
          aria-pressed={tool === "erase"}
          onClick={() => setTool((value) => (value === "erase" ? "orbit" : "erase"))}
          data-testid="forgelink-mesh-eraser"
        >
          Eraser
        </button>
        <label className="fld-mesh-scale">
          Size
          <input type="range" min="0.25" max="4" step="0.05" value={brushScale} onChange={(event) => setBrushScale(Number(event.target.value))} data-testid="forgelink-mesh-brush" />
        </label>
        <span className="fld-mesh-colors" role="group" aria-label="Highlight color">
          {HIGHLIGHT_COLORS.map((swatch) => (
            <button
              type="button"
              key={swatch.id}
              className={color === swatch.value ? "is-on" : ""}
              style={{ background: swatch.value }}
              aria-label={`Highlight ${swatch.label}`}
              aria-pressed={color === swatch.value}
              onClick={() => setColor(swatch.value)}
              data-testid={`forgelink-mesh-color-${swatch.id}`}
            />
          ))}
        </span>
        <button type="button" className="btn small" disabled={!strokeCount || busy} onClick={() => engineRef.current?.undo()} data-testid="forgelink-mesh-undo">
          Undo
        </button>
        <button type="button" className="btn small" disabled={!redoCount || busy} onClick={() => engineRef.current?.redo()} data-testid="forgelink-mesh-redo">
          Redo
        </button>
        <button type="button" className="btn small" disabled={!strokeCount || busy} onClick={() => engineRef.current?.clear()} data-testid="forgelink-mesh-clear">
          Clear
        </button>
        <input
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={500}
          placeholder="Change note for Meshy / AI Studio"
          aria-label="Change note"
          data-testid="forgelink-mesh-note"
        />
        <button type="button" className="btn small primary" disabled={!modelReady || busy || !note.trim()} onClick={send} data-testid="forgelink-mesh-send">
          {busy ? "Sending…" : "Send for reprocess"}
        </button>
        <button type="button" className="btn small" disabled={!modelReady || busy || !selectedMesh || selectedMesh.id === "sample"} onClick={sendToOrca} data-testid="forgelink-mesh-orca">
          Send to Orca
        </button>
        <p className="fld-mesh-status" data-testid="forgelink-mesh-status" aria-live="polite">
          {loadError || (tool === "paint" ? "Paint mode. Escape returns to orbit." : tool === "erase" ? "Eraser removes paint marks. Escape returns to orbit." : tool === "measure" ? "Click two points. Distance uses file units as millimetres." : status || (modelReady ? `${sizeLabel ? `${sizeLabel}. ` : ""}Drag to orbit. Plate is the A1 256 mm bed. Rotate, scale, and lay flat are view-only.` : "Open the sample wedge or load an STL, GLB, or 3MF."))}
        </p>
        {handoff ? (
          <div className="fld-mesh-recipe" data-testid="forgelink-mesh-orca-recipe">
            <b>{handoff.active ? "Purge recipe active" : "Purge recipe inactive"}</b>
            <span>{handoff.note}</span>
            {handoff.warnings?.length ? (
              <p className="fld-mesh-warn" data-testid="forgelink-mesh-orca-warning">{handoff.warnings.join(" ")} {handoff.suggestions?.join(" ") || ""}</p>
            ) : null}
          </div>
        ) : null}
        {pkg?.status === "ready_to_send" ? (
          <span className="fld-mesh-downloads">
            <button type="button" className="btn small" onClick={() => downloadSlot("angle_current")}>Current view</button>
            <button type="button" className="btn small" onClick={() => downloadSlot("angle_orbit")}>Second angle</button>
            <button type="button" className="btn small" onClick={() => downloadSlot("mask")}>Paint mask</button>
          </span>
        ) : null}
      </div>
    </div>
  );
}
